import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, from, switchMap, catchError } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { LoggingService } from '../services/logging/logging.service';

/**
 * Modern functional HTTP interceptor for handling 401 errors.
 *
 * Features:
 * - Handles 401 Unauthorized errors
 * - Attempts to refresh the token automatically
 * - Retries the original request with new token
 * - Redirects to login if refresh fails (via full page reload)
 * - Prevents concurrent refresh attempts
 *
 * CRITICAL: This interceptor MUST run AFTER authInterceptor
 * so that the token is already attached to the request.
 *
 * NOTE: Does NOT inject Router to avoid circular dependency.
 * Uses window.location.reload() for logout redirects instead.
 */

let isRefreshing = false; // Module-level flag to prevent concurrent refreshes

export const authErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const logger = inject(LoggingService);

  return next(req).pipe(
    catchError((error: unknown) => {
      // Only handle 401 errors
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handle401Error(req, next, authService, logger);
      }

      // Pass through other errors
      return throwError(() => error);
    })
  );
};

/**
 * Handle 401 errors by attempting to refresh the token
 */
function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  logger: LoggingService
): Observable<HttpEvent<unknown>> {
  // Prevent concurrent refresh attempts
  if (isRefreshing) {
    logger.debug('AuthErrorInterceptor', 'Token refresh already in progress, failing request');
    return throwError(() => new Error('Token refresh already in progress'));
  }

  // Skip refresh for token endpoints (would cause infinite loop)
  if (isTokenEndpoint(req.url)) {
    logger.debug('AuthErrorInterceptor', 'Token endpoint failed, reloading page');
    authService.logout();
    window.location.reload();
    return throwError(() => new Error('Token refresh endpoint failed'));
  }

  isRefreshing = true;
  logger.info('AuthErrorInterceptor', 'Handling 401 error - attempting token refresh');

  return from(authService.getValidAccessToken()).pipe(
    switchMap((token) => {
      isRefreshing = false;

      if (token && token !== 'secure' && token !== 'httponly') {
        // Token refreshed successfully, retry the request
        logger.info('AuthErrorInterceptor', 'Token refreshed, retrying request');

        const newRequest = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });

        return next(newRequest);
      } else {
        // Token refresh failed, reload page to show login
        logger.warn('AuthErrorInterceptor', 'Token refresh failed - reloading page');
        authService.logout();
        window.location.reload();

        return throwError(() => new HttpErrorResponse({
          error: 'Token refresh failed',
          status: 401,
          statusText: 'Unauthorized'
        }));
      }
    }),
    catchError((error) => {
      isRefreshing = false;

      logger.error('AuthErrorInterceptor', 'Token refresh error', error);
      authService.logout();
      window.location.reload();

      return throwError(() => error);
    })
  );
}

/**
 * Check if URL is a token endpoint
 */
function isTokenEndpoint(url: string): boolean {
  const tokenPaths = [
    '/auth/refresh',
    '/auth/logout',
    '/oauth2/',
  ];

  return tokenPaths.some(path => url.includes(path));
}
